import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Field, FormError } from '@/components/form';
import { AuthShell } from '@/features/auth/auth-shell';
import { RecoveryCodes } from '@/features/auth/recovery-codes';
import { SignOutLink } from '@/features/auth/sign-out-link';
import { api, errorMessage } from '@/lib/api';
import { meQuery } from '@/lib/auth';

export const Route = createFileRoute('/setup-2fa')({
  validateSearch: (s: Record<string, unknown>): { redirect?: string } => ({
    redirect: typeof s.redirect === 'string' && s.redirect.startsWith('/') ? s.redirect : undefined,
  }),
  beforeLoad: async ({ context, search }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (!me) throw redirect({ to: '/login', search });
    if (me.mfaVerified) throw redirect({ to: '/' });
    if (me.mfaEnrolled) throw redirect({ to: '/verify', search });
  },
  component: SetupPage,
});

interface Enrolment {
  secret: string;
  otpauthUri: string;
  qrCodeDataUrl: string;
}

function SetupPage() {
  const { redirect: next } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [saved, setSaved] = useState(false);

  // A query (not a mutation) so a double mount can't issue two different secrets.
  const start = useQuery({
    queryKey: ['mfa-enrolment'],
    queryFn: () => api.post<Enrolment>('/v1/auth/mfa/enrol/start'),
    staleTime: Infinity,
    retry: false,
  });
  const confirm = useMutation({
    mutationFn: () => api.post<{ recoveryCodes: string[] }>('/v1/auth/mfa/enrol/confirm', { code }),
    onSuccess: (res) => setCodes(res.recoveryCodes),
    onError: () => setCode(''),
  });

  if (codes) {
    return (
      <AuthShell
        title="Save your recovery codes"
        description="If you lose your phone, each of these codes lets you sign in once. Store them somewhere safe, like a password manager. You won’t see them again."
      >
        <div className="grid gap-5">
          <RecoveryCodes codes={codes} />
          <div className="flex items-center gap-2">
            <Checkbox id="saved" checked={saved} onCheckedChange={(v) => setSaved(v === true)} />
            <Label htmlFor="saved">I’ve saved my recovery codes</Label>
          </div>
          <Button
            size="lg"
            disabled={!saved}
            onClick={async () => {
              queryClient.removeQueries({ queryKey: ['mfa-enrolment'] });
              queryClient.removeQueries({ queryKey: ['me'] });
              await navigate({ to: next ?? '/' });
            }}
          >
            Continue to Fraud Safe
          </Button>
        </div>
      </AuthShell>
    );
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    confirm.mutate();
  }

  return (
    <AuthShell
      title="Set up two-step verification"
      description="Fraud Safe needs a code from an authenticator app (such as Google Authenticator, Microsoft Authenticator or 1Password) each time you sign in."
      footer={<SignOutLink label="Cancel and sign out" />}
    >
      <form onSubmit={submit} className="grid gap-5" noValidate>
        <FormError message={start.isError ? errorMessage(start.error) : confirm.isError ? errorMessage(confirm.error) : null} />
        <ol className="grid gap-4 text-sm">
          <li className="grid gap-3">
            <span>
              <strong className="font-medium">1.</strong> Scan this QR code with your authenticator app.
            </span>
            <div className="flex justify-center rounded-lg border bg-white p-3">
              {start.data ? (
                <img src={start.data.qrCodeDataUrl} alt="QR code for your authenticator app" className="size-44" />
              ) : (
                <Skeleton className="size-44" />
              )}
            </div>
            {start.data ? (
              <details className="text-muted-foreground">
                <summary className="cursor-pointer">Can’t scan it? Enter this key instead</summary>
                <code className="mt-2 block rounded bg-muted p-2 font-mono text-xs break-all text-foreground">{start.data.secret}</code>
              </details>
            ) : null}
          </li>
          <li className="grid gap-2">
            <span>
              <strong className="font-medium">2.</strong> Enter the 6-digit code it shows.
            </span>
            <Field id="code" label={<span className="sr-only">Authentication code</span>}>
              <Input
                id="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="123456"
                className="font-mono text-lg tracking-[0.3em]"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              />
            </Field>
          </li>
        </ol>
        <Button type="submit" size="lg" disabled={!start.data || code.length !== 6 || confirm.isPending}>
          {confirm.isPending ? 'Checking…' : 'Turn on two-step verification'}
        </Button>
      </form>
    </AuthShell>
  );
}
